import type { ComponentType } from 'react';
import { createElement } from 'react';
import { IconModule, ScreenLayout } from '@taki/ui';
import type { ShellAppEntry, ShellAppRegistry } from './types.js';

/**
 * Registre de repli integre. En v0 le shell ne resout pas encore de vrais
 * bundles depuis `taki.manifest.json` ; a la place, pour chaque id declare
 * dans `config.installedApps` sans entree de registre explicite, on monte un
 * ecran placeholder (avec une icone neutre) pour que le routage, la liste
 * d'apps et l'auth guard soient exercables de bout en bout. Les vraies apps
 * surchargent les entrees via la prop `apps` de `TakiShell`.
 */

function humanizeId(id: string): string {
  const cleaned = id.replace(/[-_]+/g, ' ').trim();
  if (cleaned.length === 0) return id;
  return cleaned
    .split(' ')
    .map((word) => (word.length > 0 ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(' ');
}

function makePlaceholderComponent(id: string): ComponentType {
  // Meme un placeholder respecte le gabarit d'ecran standard (ScreenLayout,
  // cf. docs/GABARIT-ECRAN.md) : titre en zone 2, etat vide standard en zone 5.
  const Placeholder: ComponentType = () =>
    createElement(ScreenLayout, {
      title: humanizeId(id),
      empty: true,
      emptyTitle: 'Bientot disponible',
      emptyHint: `App "${id}" est installee mais n'a pas encore de bundle enregistre dans le shell.`,
    });
  Placeholder.displayName = `PlaceholderApp(${id})`;
  return Placeholder;
}

/**
 * Resout une {@link ShellAppEntry} concrete pour un id d'app installe, en
 * preferant une surcharge fournie par l'appelant et en retombant sur un
 * placeholder genere (icone module neutre).
 */
export function resolveAppEntry(
  id: string,
  overrides: ShellAppRegistry | undefined,
): ShellAppEntry {
  const override = overrides?.[id];
  if (override) return override;

  const Component = makePlaceholderComponent(id);
  return {
    id,
    label: humanizeId(id),
    icon: createElement(IconModule, { size: 22 }),
    load: async () => ({ default: Component }),
  };
}

/**
 * Construit la liste ordonnee des entrees d'apps que le shell doit exposer,
 * une par id de `installedApps`. L'ordre suit `installedApps` pour que la
 * liste et le lanceur restent stables et previsibles.
 */
export function buildAppEntries(
  installedApps: readonly string[],
  overrides: ShellAppRegistry | undefined,
): ShellAppEntry[] {
  return installedApps.map((id) => resolveAppEntry(id, overrides));
}
